import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import ReactQuill from "react-quill";
import Quill from "quill";
import "react-quill/dist/quill.snow.css";
import Swal from "sweetalert2";
import Attachments from "./Attachments";
import { UrlContext } from "../../contexts/UrlContext";
import { AppContext } from "../../contexts/AppContext";

const Block = Quill.import("blots/block");
Block.tagName = "div";
Quill.register(Block, true);

const Font = Quill.import("formats/font");
Font.whitelist = [
  "arial",
  "georgia",
  "times-new-roman",
  "courier-new",
  "verdana",
  "tahoma",
  "lucida",
];
Quill.register(Font, true);

const EMAIL_PATTERN = /^[^\s@<>;,]+@[^\s@<>;,]+\.[^\s@<>;,]+$/;
const FIELD_LABELS = { to: "To", cc: "Cc", bcc: "Bcc" };

function uniqueEmails(values = []) {
  const seen = new Set();
  return values
    .map((value) => String(value || "").trim())
    .filter((value) => {
      const key = value.toLowerCase();
      if (!value || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function signatureHtmlWithValues(data = {}) {
  const signature = String(data.signature || "");
  const user = data.user || {};
  return signature
    .replace(
      "{{name}}",
      `${user.sigtitle || ""} ${user.sigfirstname || ""} ${user.siglastname || ""}`.trim()
    )
    .replace("{{designation}}", user.config || "")
    .replace("{{contactNumber}}", user.path_z || "");
}

export default function ProjectEmailComposer({
  mode = "compose",
  mailboxes = [],
  initialMailboxEmail = "",
  initialTo = [],
  initialCc = [],
  initialBcc = [],
  initialSubject = "",
  initialBody = "",
  originalMessage = null,
  projectName = "",
  projectID = null,
  sending = false,
  onCancel,
  onSend,
}) {
  const { API_URL } = useContext(UrlContext);
  const { allEmails } = useContext(AppContext);

  const [mailboxEmail, setMailboxEmail] = useState(initialMailboxEmail);
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [toRecipients, setToRecipients] = useState(uniqueEmails(initialTo));
  const [ccRecipients, setCcRecipients] = useState(uniqueEmails(initialCc));
  const [bccRecipients, setBccRecipients] = useState(uniqueEmails(initialBcc));
  const [showCc, setShowCc] = useState(initialCc.length > 0);
  const [showBcc, setShowBcc] = useState(initialBcc.length > 0);
  const [includeSignature, setIncludeSignature] = useState(true);
  const [signatureHtml, setSignatureHtml] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [openDragDrop, setOpenDragDrop] = useState(false);
  const [attachmentModalSource, setAttachmentModalSource] = useState("upload");
  const [recipientInputs, setRecipientInputs] = useState({ to: "", cc: "", bcc: "" });
  const [recipientErrors, setRecipientErrors] = useState({ to: "", cc: "", bcc: "" });
  const [focusedRecipientField, setFocusedRecipientField] = useState(null);
  const [hoveredRecipientField, setHoveredRecipientField] = useState(null);
  const [isBodyDragActive, setIsBodyDragActive] = useState(false);

  const signatureRef = useRef(null);
  const bodyDragDepthRef = useRef(0);

  const actionTitle = {
    compose: "New client email",
    reply: "Reply",
    replyAll: "Reply all",
    forward: "Forward",
  }[mode];

  useEffect(() => {
    setMailboxEmail(initialMailboxEmail || mailboxes[0]?.email || "");
    setSubject(initialSubject || "");
    setBody(initialBody || "");
    setToRecipients(uniqueEmails(initialTo));
    setCcRecipients(uniqueEmails(initialCc));
    setBccRecipients(uniqueEmails(initialBcc));
    setShowCc(initialCc.length > 0);
    setShowBcc(initialBcc.length > 0);
    setAttachments([]);
  }, [
    mode,
    initialMailboxEmail,
    initialSubject,
    initialBody,
    JSON.stringify(initialTo),
    JSON.stringify(initialCc),
    JSON.stringify(initialBcc),
  ]);

  useEffect(() => {
    const fetchSignature = async () => {
      const tokenObj = localStorage.getItem("eph-token");
      if (!tokenObj) return;

      try {
        const token = JSON.parse(tokenObj)?.value;
        const response = await fetch(`${API_URL}account-settings`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": token,
          },
        });
        const data = await response.json();
        setSignatureHtml(signatureHtmlWithValues(data));
      } catch (error) {
        console.error("Could not load signature:", error);
      }
    };

    fetchSignature();
  }, [API_URL]);

  useEffect(() => {
    if (!signatureRef.current) return;

    const images = Array.from(signatureRef.current.querySelectorAll("img"));
    const lastThreeImages = images.slice(-3);

    images.forEach((image) => {
      image.style.width = "auto";
      image.style.height = "auto";
      image.style.maxWidth = "100%";
      image.style.objectFit = "contain";
    });

    lastThreeImages.forEach((image, index) => {
      image.style.maxWidth = index === 1 ? "95px" : "100%";
      image.style.maxHeight = index === 1 ? "150px" : "1000px";
      if (index === 1) image.style.marginLeft = "2px";
    });
  }, [signatureHtml, includeSignature]);

  useEffect(() => {
    const reset = () => {
      bodyDragDepthRef.current = 0;
      setIsBodyDragActive(false);
    };

    window.addEventListener("drop", reset);
    window.addEventListener("dragend", reset);
    return () => {
      window.removeEventListener("drop", reset);
      window.removeEventListener("dragend", reset);
    };
  }, []);

  const getFieldEmails = (field) => {
    if (field === "to") return toRecipients;
    if (field === "cc") return ccRecipients;
    return bccRecipients;
  };

  const setFieldEmails = (field, updater) => {
    if (field === "to") setToRecipients(updater);
    else if (field === "cc") setCcRecipients(updater);
    else setBccRecipients(updater);
  };

  const allRecipientEmails = () => [
    ...toRecipients,
    ...ccRecipients,
    ...bccRecipients,
  ];

  const splitEmailInput = (value = "") =>
    String(value)
      .split(/[;,\s]+/)
      .map((item) => item.trim())
      .filter(Boolean);

  const addEmailsToField = (field, rawValue) => {
    const candidates = splitEmailInput(rawValue);
    if (!candidates.length) return false;

    const invalid = candidates.find((email) => !EMAIL_PATTERN.test(email));
    if (invalid) {
      setRecipientErrors((current) => ({
        ...current,
        [field]: `Invalid email: ${invalid}`,
      }));
      return false;
    }

    const existing = new Set(
      allRecipientEmails().map((email) => email.toLowerCase())
    );
    const additions = candidates.filter((email) => {
      const key = email.toLowerCase();
      if (existing.has(key)) return false;
      existing.add(key);
      return true;
    });

    if (!additions.length) {
      setRecipientErrors((current) => ({
        ...current,
        [field]: "This email is already added.",
      }));
      return false;
    }

    setFieldEmails(field, (current) => [...current, ...additions]);
    setRecipientErrors((current) => ({ ...current, [field]: "" }));
    return true;
  };

  const removeEmailFromField = (field, email) => {
    setFieldEmails(field, (current) =>
      current.filter((item) => item.toLowerCase() !== email.toLowerCase())
    );
  };

  const moveEmailBetweenFields = (sourceField, targetField, email) => {
    if (!email || sourceField === targetField) return;
    removeEmailFromField(sourceField, email);
    setFieldEmails(targetField, (current) =>
      current.some((item) => item.toLowerCase() === email.toLowerCase())
        ? current
        : [...current, email]
    );
  };

  const commitRecipientInput = (field) => {
    const value = recipientInputs[field];
    if (!value.trim()) return;
    if (addEmailsToField(field, value)) {
      setRecipientInputs((current) => ({ ...current, [field]: "" }));
    }
  };

  const suggestionsFor = (field) => {
    const query = recipientInputs[field].trim().toLowerCase();
    if (query.length < 2) return [];

    const existing = new Set(
      allRecipientEmails().map((email) => email.toLowerCase())
    );

    return (allEmails || [])
      .filter((contact) => {
        const email = String(contact?.email || "").trim();
        const name = String(contact?.name || "").trim();
        if (!email || existing.has(email.toLowerCase()) || !EMAIL_PATTERN.test(email)) {
          return false;
        }
        return `${name} ${email}`.toLowerCase().includes(query);
      })
      .slice(0, 8);
  };

  const avatarClass = (email = "") => {
    const classes = [
      "bg-[#365561]",
      "bg-slate-600",
      "bg-cyan-700",
      "bg-emerald-700",
      "bg-indigo-700",
      "bg-rose-700",
      "bg-amber-700",
      "bg-violet-700",
    ];
    const index = String(email)
      .split("")
      .reduce((sum, character) => sum + character.charCodeAt(0), 0);
    return classes[index % classes.length];
  };

  const renderRecipientField = (field, placeholder) => {
    const emails = getFieldEmails(field);
    const isFocused = focusedRecipientField === field;
    const isHovered = hoveredRecipientField === field;
    const showLabel = isFocused || isHovered || emails.length > 0 || recipientInputs[field];
    const suggestions = suggestionsFor(field);

    return (
      <div
        className="border-b border-slate-200 px-3 py-2 sm:px-4"
        onMouseEnter={() => setHoveredRecipientField(field)}
        onMouseLeave={() => setHoveredRecipientField(null)}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          try {
            const payload = JSON.parse(
              event.dataTransfer.getData("application/json")
            );
            moveEmailBetweenFields(payload.sourceField, field, payload.email);
          } catch {
            addEmailsToField(field, event.dataTransfer.getData("text/plain"));
          }
        }}
      >
        <div className={showLabel ? "grid grid-cols-[22px_minmax(0,1fr)] gap-2" : "grid grid-cols-1"}>
          {showLabel && (
            <span className="pt-1 text-sm font-semibold text-slate-500">
              {FIELD_LABELS[field]}
            </span>
          )}

          <div className="relative min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {emails.map((email) => (
                <span
                  key={`${field}-${email}`}
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.setData(
                      "application/json",
                      JSON.stringify({ email, sourceField: field })
                    );
                    event.dataTransfer.setData("text/plain", email);
                  }}
                  className="group inline-flex h-7 max-w-full cursor-grab items-center overflow-hidden rounded-full border border-slate-200 bg-white text-xs font-medium text-slate-700 transition hover:bg-slate-50 hover:shadow-sm active:cursor-grabbing sm:max-w-[300px]"
                >
                  <span className={`ml-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${avatarClass(email)}`}>
                    {email.charAt(0).toUpperCase()}
                  </span>
                  <span className="min-w-0 flex-1 truncate px-1 sm:max-w-[240px]">
                    {email}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeEmailFromField(field, email)}
                    className="flex h-6 w-6 shrink-0 items-center justify-center text-slate-400 hover:text-red-500"
                  >
                    <i className="fa-solid fa-xmark text-[10px]" />
                  </button>
                </span>
              ))}

              <input
                value={recipientInputs[field]}
                onChange={(event) => {
                  setRecipientInputs((current) => ({
                    ...current,
                    [field]: event.target.value,
                  }));
                  setRecipientErrors((current) => ({ ...current, [field]: "" }));
                }}
                onFocus={() => setFocusedRecipientField(field)}
                onBlur={() => {
                  commitRecipientInput(field);
                  setFocusedRecipientField(null);
                }}
                onKeyDown={(event) => {
                  if (["Enter", "Tab", ",", ";"].includes(event.key) && recipientInputs[field].trim()) {
                    event.preventDefault();
                    commitRecipientInput(field);
                  }
                }}
                onPaste={(event) => {
                  const pasted = event.clipboardData.getData("text");
                  if (splitEmailInput(pasted).length > 1) {
                    event.preventDefault();
                    if (addEmailsToField(field, pasted)) {
                      setRecipientInputs((current) => ({ ...current, [field]: "" }));
                    }
                  }
                }}
                placeholder={emails.length ? "" : placeholder}
                className="min-w-[160px] flex-1 border-0 bg-transparent py-1 text-sm text-slate-800 outline-none placeholder:text-slate-400"
              />
            </div>

            <AnimatePresence>
              {isFocused && recipientInputs[field].trim() && suggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-[0_18px_40px_rgba(15,23,42,0.18)]"
                >
                  {suggestions.map((contact) => (
                    <button
                      key={`${field}-${contact.email}`}
                      type="button"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        if (addEmailsToField(field, contact.email)) {
                          setRecipientInputs((current) => ({ ...current, [field]: "" }));
                        }
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
                    >
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${avatarClass(contact.email)}`}>
                        {String(contact.name || contact.email).charAt(0).toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-slate-800">
                          {contact.name || contact.email}
                        </span>
                        <span className="block truncate text-xs text-slate-500">
                          {contact.email}
                        </span>
                      </span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {recipientErrors[field] && (
              <p className="mt-1 text-xs font-medium text-red-500">
                {recipientErrors[field]}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const fileToAttachment = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        resolve({
          name: file.name,
          size: file.size,
          contentType: file.type || "application/octet-stream",
          base64: result.includes(",") ? result.split(",")[1] : result,
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const addFiles = async (fileList) => {
    try {
      const next = await Promise.all(Array.from(fileList || []).map(fileToAttachment));
      setAttachments((current) => [...current, ...next]);
    } catch {
      Swal.fire("Error", "Could not attach one or more files.", "error");
    }
  };

  const send = async () => {
    const pendingByField = {
      to: splitEmailInput(recipientInputs.to),
      cc: splitEmailInput(recipientInputs.cc),
      bcc: splitEmailInput(recipientInputs.bcc),
    };

    const invalidPending = Object.entries(pendingByField).find(([, values]) =>
      values.some((email) => !EMAIL_PATTERN.test(email))
    );

    if (invalidPending) {
      const [field, values] = invalidPending;
      const invalidEmail = values.find((email) => !EMAIL_PATTERN.test(email));
      setRecipientErrors((current) => ({
        ...current,
        [field]: `Invalid email: ${invalidEmail}`,
      }));
      return;
    }

    const finalTo = uniqueEmails([...toRecipients, ...pendingByField.to]);
    const finalCc = uniqueEmails([...ccRecipients, ...pendingByField.cc]).filter(
      (email) => !finalTo.some((item) => item.toLowerCase() === email.toLowerCase())
    );
    const finalBcc = uniqueEmails([...bccRecipients, ...pendingByField.bcc]).filter(
      (email) =>
        !finalTo.some((item) => item.toLowerCase() === email.toLowerCase()) &&
        !finalCc.some((item) => item.toLowerCase() === email.toLowerCase())
    );

    if (!finalTo.length) {
      Swal.fire("Missing recipient", "Add at least one To recipient.", "warning");
      return;
    }

    setToRecipients(finalTo);
    setCcRecipients(finalCc);
    setBccRecipients(finalBcc);
    setRecipientInputs({ to: "", cc: "", bcc: "" });

    const finalBody = includeSignature && signatureHtml
      ? `${body}<br>${signatureHtml}`
      : body;

    await onSend({
      mode,
      mailboxEmail,
      toRecipients: finalTo,
      ccRecipients: finalCc,
      bccRecipients: finalBcc,
      subject,
      body: finalBody,
      attachments,
    });
  };

  return (
    <motion.section
      layout
      transition={{ layout: { type: "spring", stiffness: 360, damping: 34 } }}
      className="flex h-full min-h-0 flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)]"
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-3 py-2 sm:px-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={sending}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-[#365561] disabled:opacity-50"
          aria-label="Back"
        >
          <i className="fa-solid fa-arrow-left text-xs" />
        </button>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900 sm:text-base">
            {subject || "(No subject)"}
          </p>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#365561]">
            {actionTitle}
          </p>
        </div>
      </div>

      <div className="shrink-0 border-b border-slate-200">

        {renderRecipientField("to", "Add recipients")}
        {showCc && renderRecipientField("cc", "Add cc recipients")}
        {showBcc && renderRecipientField("bcc", "Add bcc recipients")}

        {mode === "compose" && (
          <label className="grid grid-cols-[58px_minmax(0,1fr)] items-center border-b border-slate-200 px-3 py-2 sm:px-4">
            <span className="text-sm font-semibold text-slate-500">
              Subject
            </span>
            <input
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              placeholder={`expertpowerhouse | ${projectName || "Project"}`}
              className="min-w-0 border-0 bg-transparent py-1 text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400"
            />
          </label>
        )}

        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 bg-slate-50 px-3 py-2 sm:px-4">
            {attachments.map((attachment, index) => (
              <div key={`${attachment.name}-${index}`} className="flex max-w-[320px] items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2 shadow-sm">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                  <i className="fa-solid fa-file" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-slate-800">{attachment.name}</p>
                  <p className="text-[11px] text-slate-500">{Math.round(attachment.size / 1024)} KB</p>
                </div>
                <button type="button" onClick={() => setAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index))} className="h-7 w-7 rounded-full text-red-500 hover:bg-red-50">
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        className="relative min-h-0 flex-1 overflow-y-auto bg-white px-3 py-3 sm:px-4 sm:py-4"
        onDragEnter={(event) => {
          if (Array.from(event.dataTransfer?.types || []).includes("Files")) {
            event.preventDefault();
            bodyDragDepthRef.current += 1;
            setIsBodyDragActive(true);
          }
        }}
        onDragOver={(event) => {
          if (Array.from(event.dataTransfer?.types || []).includes("Files")) {
            event.preventDefault();
          }
        }}
        onDragLeave={() => {
          bodyDragDepthRef.current = Math.max(0, bodyDragDepthRef.current - 1);
          if (bodyDragDepthRef.current === 0) setIsBodyDragActive(false);
        }}
        onDrop={async (event) => {
          if (Array.from(event.dataTransfer?.types || []).includes("Files")) {
            event.preventDefault();
            bodyDragDepthRef.current = 0;
            setIsBodyDragActive(false);
            await addFiles(event.dataTransfer.files);
          }
        }}
      >
        <AnimatePresence>
          {isBodyDragActive && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="pointer-events-none absolute inset-3 z-40 flex items-center justify-center rounded-2xl border-2 border-dashed border-[#365561]/60 bg-white/80 text-[#365561] backdrop-blur-sm"
            >
              <div className="text-center">
                <i className="fa-solid fa-cloud-arrow-up mb-2 text-3xl" />
                <p className="text-sm font-semibold">Drop files here to attach them</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="project-email-compose-editor overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <ReactQuill
            theme="snow"
            value={body}
            onChange={setBody}
            style={{
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
            modules={{
              toolbar: [
                [{ font: Font.whitelist }],
                [{ header: [1, 2, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link"],
              ],
            }}
            formats={["font", "header", "bold", "italic", "underline", "strike", "list", "bullet", "link"]}
          />

          <div className="border-t border-slate-100 px-4 py-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-500">
              <input
                type="checkbox"
                checked={includeSignature}
                onChange={(event) => setIncludeSignature(event.target.checked)}
              />
              Include signature?
            </label>

            {includeSignature && (
              <div
                ref={signatureRef}
                className="project-email-signature mt-3 overflow-hidden"
                dangerouslySetInnerHTML={{ __html: signatureHtml }}
              />
            )}
          </div>
        </div>

        {originalMessage && mode !== "compose" && (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
            Outlook will preserve the original quoted email and conversation history.
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2 border-t border-slate-200 bg-white px-3 py-2 sm:px-4">
        <button
          type="button"
          onClick={send}
          disabled={sending || !toRecipients.length}
          className="inline-flex items-center gap-2 rounded-full bg-[#365561] px-4 py-2 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(54,85,97,0.22)] transition hover:bg-[#2c4650] disabled:cursor-not-allowed disabled:opacity-45 focus:outline-none"
        >
          <i className={`fa-solid ${sending ? "fa-spinner fa-spin" : "fa-paper-plane"}`} />
          {sending ? "Sending..." : "Send"}
        </button>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowCc((value) => !value)}
            className={[
              "border-b-2 px-1 text-xs font-semibold text-slate-500 transition hover:text-[#365561] focus:outline-none",
              showCc ? "border-[#365561] text-[#365561]" : "border-transparent",
            ].join(" ")}
            aria-label={showCc ? "Hide Cc" : "Open Cc"}
          >
            Cc
          </button>

          <button
            type="button"
            onClick={() => setShowBcc((value) => !value)}
            className={[
              "border-b-2 px-1 text-xs font-semibold text-slate-500 transition hover:text-[#365561] focus:outline-none",
              showBcc ? "border-[#365561] text-[#365561]" : "border-transparent",
            ].join(" ")}
            aria-label={showBcc ? "Hide Bcc" : "Open Bcc"}
          >
            Bcc
          </button>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                setAttachmentModalSource("upload");
                setOpenDragDrop(true);
              }}
              className="group relative inline-flex h-7 w-7 items-center justify-center rounded-full !border-0 !bg-transparent !text-slate-500 transition hover:!bg-slate-100 hover:!text-[#365561] focus:outline-none"
              aria-label="Attach files from computer"
              title="Attach files"
            >
              <i className="fa-solid fa-paperclip text-sm" />
              {attachments.length > 0 && (
                <span className="absolute -right-0.5 -top-1 text-[10px] font-bold leading-none text-[#365561]">
                  {attachments.length}
                </span>
              )}
              <span className="pointer-events-none absolute right-0 bottom-full z-50 mt-2 hidden whitespace-nowrap rounded-lg bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-lg group-hover:block">
                Attach from computer
              </span>
            </button>

            <button
              type="button"
              disabled={!projectID}
              onClick={() => {
                setAttachmentModalSource("sharepoint");
                setOpenDragDrop(true);
              }}
              className="group relative inline-flex h-7 w-7 items-center justify-center rounded-full !border-0 !bg-transparent !text-slate-500 transition hover:!bg-slate-100 hover:!text-[#365561] disabled:cursor-not-allowed disabled:!text-slate-300 disabled:hover:!bg-transparent focus:outline-none"
              aria-label="Attach project files from SharePoint"
              title={projectID ? "Project files" : "Project files require a project"}
            >
              <i className="fa-solid fa-folder-open text-sm" />
              <span className="pointer-events-none absolute right-0 bottom-full z-50 mt-2 hidden whitespace-nowrap rounded-lg bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-lg group-hover:block">
                {projectID ? "Project files" : "No project selected"}
              </span>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .project-email-compose-editor .ql-editor {
          font-family: Arial, sans-serif;
          font-size: 14px;
          line-height: 1.45;
          min-height: 220px;
          padding: 14px 16px !important;
        }

        .project-email-compose-editor .ql-container {
          height: auto !important;
          min-height: 220px;
          border: 0 !important;
          padding-bottom: 10px;
        }

        .project-email-compose-editor .ql-container .ql-snow .ql-hidden {
          display: none !important;
        }

        .project-email-compose-editor .ql-toolbar.ql-snow {
          position: inherit;
          top: 0;
          right: 24px;
          padding: 6px 8px;
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          border: 0 !important;
          border-bottom: 1px solid rgb(226 232 240) !important;
          background: rgb(248 250 252);
        }

        .project-email-compose-editor .ql-font-arial { font-family: Arial, sans-serif; }
        .project-email-compose-editor .ql-font-georgia { font-family: Georgia, serif; }
        .project-email-compose-editor .ql-font-times-new-roman { font-family: "Times New Roman", serif; }
        .project-email-compose-editor .ql-font-courier-new { font-family: "Courier New", monospace; }
        .project-email-compose-editor .ql-font-verdana { font-family: Verdana, sans-serif; }
        .project-email-compose-editor .ql-font-tahoma { font-family: Tahoma, sans-serif; }
        .project-email-compose-editor .ql-font-lucida { font-family: "Lucida Sans Unicode", sans-serif; }

        .project-email-compose-editor .ql-snow .ql-picker.ql-font .ql-picker-label::before,
        .project-email-compose-editor .ql-snow .ql-picker.ql-font .ql-picker-item::before {
          content: attr(data-value);
          font-family: inherit;
        }

        .project-email-compose-editor .ql-picker-item[data-value="arial"]::before,
        .project-email-compose-editor .ql-picker-label[data-value="arial"]::before {
          font-family: Arial, sans-serif;
          content: "Arial";
        }

        .project-email-compose-editor .ql-picker-item[data-value="georgia"]::before,
        .project-email-compose-editor .ql-picker-label[data-value="georgia"]::before {
          font-family: Georgia, serif;
          content: "Georgia";
        }

        .project-email-compose-editor .ql-picker-item[data-value="times-new-roman"]::before,
        .project-email-compose-editor .ql-picker-label[data-value="times-new-roman"]::before {
          font-family: "Times New Roman", serif;
          content: "Times New Roman";
        }

        .project-email-compose-editor .ql-picker-item[data-value="courier-new"]::before,
        .project-email-compose-editor .ql-picker-label[data-value="courier-new"]::before {
          font-family: "Courier New", monospace;
          content: "Courier New";
        }

        .project-email-compose-editor .ql-picker-item[data-value="verdana"]::before,
        .project-email-compose-editor .ql-picker-label[data-value="verdana"]::before {
          font-family: Verdana, sans-serif;
          content: "Verdana";
        }

        .project-email-compose-editor .ql-picker-item[data-value="tahoma"]::before,
        .project-email-compose-editor .ql-picker-label[data-value="tahoma"]::before {
          font-family: Tahoma, sans-serif;
          content: "Tahoma";
        }

        .project-email-compose-editor .ql-picker-item[data-value="lucida"]::before,
        .project-email-compose-editor .ql-picker-label[data-value="lucida"]::before {
          font-family: "Lucida Sans Unicode", sans-serif;
          content: "Lucida";
        }

        .project-email-compose-editor .ql-editor ul {
          padding-left: 1.25em;
          margin: 0.5em 0;
        }

        .project-email-compose-editor .ql-container {
          position: relative;
        }

        .project-email-compose-editor .ql-tooltip {
          left: 50% !important;
          right: auto !important;
          transform: translate(-50%, 10px) !important;
        }

        .project-email-compose-editor .ql-tooltip.ql-flip {
          left: 50% !important;
          right: auto !important;
          transform: translate(-50%, -10px) !important;
        }

        .project-email-signature img {
          width: auto !important;
          height: auto !important;
          max-width: 100% !important;
          object-fit: contain;
        }

        .project-email-signature table {
          width: auto !important;
          max-width: 100% !important;
        }
      `}</style>

      <AnimatePresence>
        {openDragDrop && (
          <Attachments
            key={`project-email-attachments-${attachmentModalSource}`}
            setOpenDragDrop={setOpenDragDrop}
            setBase64Strings={setAttachments}
            setHasAttachment={() => {}}
            projectID={projectID}
            initialSource={attachmentModalSource}
          />
        )}
      </AnimatePresence>
    </motion.section>
  );
}
